<script setup lang="ts">
import { Gesuch, GesuchPhase } from '@/models/Gesuch'
import { ref, onMounted, watch } from 'vue'
import { useAuthUser } from '@/helpers/globals'
import { useI18n } from 'vue-i18n'
import type { Ref } from 'vue'
import { Mitteilung, MitteilungCollection } from '@/models/Mitteilung'
import { instance as api } from '@/helpers/api'
import { updateUnreadMessages } from '@/composables/unreadMessages'
import MitteilungForm from '@/components/mitteilung/mitteilung-form.vue'
import MitteilungItem from '@/components/mitteilung/mitteilung-item.vue'

const { gesuch, loading = false } = defineProps<{
    gesuch: Gesuch | null
    loading: boolean
}>()
const showMitteilungForm = ref(false)
const msgLoading = ref(false)
const empfaengerlist = ref([])
const activeCard: Ref<number | null> = ref(null)
const closed = ref(false)

const user = useAuthUser()
const { t } = useI18n()

const mitteilungen = new MitteilungCollection()
watch(activeCard, (val) => {
    if (typeof val === 'undefined' || val === null) {
        return
    }
    if (val >= 0) {
        const msg = mitteilungen.getModels()[val]
        if (
            user?.id &&
            msg.empfaenger_apollon_ids &&
            msg.empfaenger_apollon_ids.indexOf(user.id) >= 0 &&
            (!msg.gelesen_apollon_ids || msg.gelesen_apollon_ids.indexOf(user.id) < 0)
        ) {
            setMessageRead(msg)
        }
    }
})

onMounted(() => {
    closed.value = gesuch?.phase === GesuchPhase.FERTIG
    loadMitteilungen()
    loadEmpfaenger()
})

async function loadMitteilungen() {
    updateUnreadMessages()
    msgLoading.value = true
    try {
        await mitteilungen.query({
            filter: [
                {
                    property: 'gesuch_id',
                    value: gesuch?.id,
                },
            ],
        })
    } catch (e) {
        console.error(e)
    } finally {
        msgLoading.value = false
    }
}

function openMitteilungForm() {
    showMitteilungForm.value = true
}

function closeMitteilungForm() {
    showMitteilungForm.value = false
}

async function loadEmpfaenger() {
    const response = await api().post('/mitteilung/getEmpfaenger', {
        gesuchId: gesuch?.id,
        filterOutUser: true,
    })
    empfaengerlist.value = response.data.roles.map((val: string) => {
        return { title: getRolleBezeichnung(val), value: val }
    })
}

async function setMessageRead(msg: Mitteilung) {
    await api().post('/mitteilung/setMitteilungRead', {
        mitteilungId: msg.id,
    })
    loadMitteilungen()
}

function onMsgDestroyed() {
    activeCard.value = null
    loadMitteilungen()
}

function onMsgSent() {
    showMitteilungForm.value = false
    loadMitteilungen()
}

function getRolleBezeichnung(rolle: string) {
    if (rolle !== 'gesuchspruefer') {
        if (
            gesuch?.config?.config?.kontakte?.get(rolle) &&
            gesuch?.config?.config?.kontakte?.get(rolle)?.label
        ) {
            return gesuch.config.config.kontakte.get(rolle).label
        }
    }
    return t('apollon_rolle_' + rolle)
}
</script>

<template>
    <div class="centered-card">
        <v-container fluid>
            <v-card-actions>
                <v-spacer />
                <v-btn
                    color="action white--text"
                    :disabled="loading || closed"
                    class="mb-5"
                    variant="flat"
                    @click="openMitteilungForm"
                    append-icon="fas fa-plus"
                >
                    {{ t('create_message') }}
                </v-btn>
            </v-card-actions>
            <v-expansion-panels v-model="activeCard">
                <MitteilungItem
                    v-for="mitteilung in mitteilungen.getModels()"
                    :key="mitteilung.id || 'new'"
                    :gesuch="gesuch"
                    :mitteilung="mitteilung"
                    @mitteilungdestroyed="onMsgDestroyed"
                />
            </v-expansion-panels>
            <MitteilungForm
                v-if="showMitteilungForm"
                v-model="showMitteilungForm"
                :gesuch="gesuch"
                :empfaengerlist="empfaengerlist"
                @close="closeMitteilungForm"
                @mitteilungSent="onMsgSent"
            ></MitteilungForm>
        </v-container>
    </div>
</template>

<style lang="sass" scoped></style>
